import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar as CalendarIcon, List, MapPin, Clock, Star, ChevronLeft, ChevronRight, Grid3X3 } from 'lucide-react';
import { eventApi, categoriesApi } from '../services/api';
import { format, parseISO, isSameDay, isSameMonth, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isToday } from 'date-fns';

const EventsCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [view, setView] = useState('list');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedCategory]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [eventsRes, catsRes] = await Promise.all([
        eventApi.getAll({ category: selectedCategory || undefined }),
        categoriesApi.getEventCategories()
      ]);
      setEvents(eventsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatEventDate = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      return {
        month: format(date, 'MMM'),
        day: format(date, 'd'),
        full: format(date, 'EEEE, MMMM d, yyyy'),
        date: date
      };
    } catch {
      return { month: 'TBD', day: '--', full: dateStr, date: null };
    }
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      try {
        return isSameDay(parseISO(event.event_date), date);
      } catch {
        return false;
      }
    });
  };

  // Build calendar grid days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calStart = startOfWeek(monthStart);
    const calEnd = endOfWeek(monthEnd);
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [currentMonth]);

  const filteredEvents = selectedDate
    ? events.filter(e => { try { return isSameDay(parseISO(e.event_date), selectedDate); } catch { return false; } })
    : events;

  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Category colors for event dots
  const categoryColors = {
    'Music & Entertainment': '#8B5CF6',
    'Food & Dining': '#F59E0B',
    'Community & Civic': '#005AA0',
    'Sports & Recreation': '#10B981',
    'Arts & Culture': '#EC4899',
    'Faith & Worship': '#6366F1',
    'Business & Networking': '#F08C28',
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="events-calendar">
      <Helmet>
        <title>Community Events in Martinsville & Henry County, VA | Explore MHC</title>
        <meta name="description" content="Find upcoming events, festivals, markets, and community gatherings in Martinsville and Henry County, Virginia. Never miss what's happening in MHC." />
        <link rel="canonical" href="https://exploremhc.com/events" />
        <meta property="og:title" content="Community Events in Martinsville & Henry County, VA" />
        <meta property="og:description" content="Find upcoming events, festivals, markets, and community gatherings in Martinsville and Henry County, Virginia." />
        <meta property="og:image" content="https://images.pexels.com/photos/17591568/pexels-photo-17591568.jpeg?auto=compress&cs=tinysrgb&w=1200&h=630&fit=crop" />
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-3" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Events Calendar
          </h1>
          <p className="text-lg text-neutral-600 mb-6">
            Explore what's happening in Martinsville and Henry County
          </p>

          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex bg-neutral-100 rounded-xl p-1 gap-1">
              <button
                onClick={() => { setView('list'); setSelectedDate(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${view === 'list' ? 'bg-white text-[#005AA0] shadow-sm' : 'text-neutral-500 hover:text-neutral-700'}`}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" /> List
              </button>
              <button
                onClick={() => setView('calendar')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${view === 'calendar' ? 'bg-white text-[#005AA0] shadow-sm' : 'text-neutral-500 hover:text-neutral-700'}`}
                data-testid="view-calendar-btn"
              >
                <Grid3X3 className="w-4 h-4" /> Calendar
              </button>
            </div>

            <div className="flex gap-3 items-center">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="h-10 px-3 rounded-lg border border-neutral-200 text-sm bg-white focus:ring-2 focus:ring-[#005AA0]/20 focus:border-[#005AA0] outline-none"
                data-testid="category-filter"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>

              {selectedDate && (
                <button
                  onClick={() => setSelectedDate(null)}
                  className="h-10 px-4 rounded-lg border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50 bg-white"
                  data-testid="clear-date-btn"
                >
                  Clear Date
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-[#005AA0]/20 border-t-[#005AA0] rounded-full animate-spin"></div>
          </div>
        ) : view === 'calendar' ? (
          /* ==================== CALENDAR GRID VIEW ==================== */
          <div>
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center hover:bg-neutral-50 transition-colors"
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="w-5 h-5 text-neutral-600" />
              </button>
              <h2 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'DM Serif Display, serif' }}>
                {format(currentMonth, 'MMMM yyyy')}
              </h2>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center hover:bg-neutral-50 transition-colors"
                data-testid="next-month-btn"
              >
                <ChevronRight className="w-5 h-5 text-neutral-600" />
              </button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 mb-2">
              {WEEKDAYS.map(day => (
                <div key={day} className="text-center text-xs font-bold text-neutral-400 uppercase tracking-wider py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 border border-neutral-200 rounded-2xl overflow-hidden bg-white" data-testid="calendar-grid">
              {calendarDays.map((day, idx) => {
                const dayEvents = getEventsForDate(day);
                const inMonth = isSameMonth(day, currentMonth);
                const today = isToday(day);
                const isSelected = selectedDate && isSameDay(day, selectedDate);

                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedDate(isSelected ? null : day)}
                    className={`min-h-[100px] md:min-h-[120px] border-b border-r border-neutral-100 p-2 cursor-pointer transition-colors
                      ${!inMonth ? 'bg-neutral-50/60' : 'hover:bg-[#005AA0]/[0.03]'}
                      ${isSelected ? 'bg-[#005AA0]/[0.06] ring-2 ring-inset ring-[#005AA0]/30' : ''}
                    `}
                    data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <div className={`text-right mb-1`}>
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-semibold
                        ${today ? 'bg-[#005AA0] text-white' : ''}
                        ${!inMonth ? 'text-neutral-300' : 'text-neutral-700'}
                      `}>
                        {format(day, 'd')}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((event) => (
                        <Link
                          key={event.id}
                          to={`/events/${event.slug || event.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className={`block px-1.5 py-0.5 rounded text-xs font-medium truncate transition-opacity hover:opacity-80
                            ${event.is_featured ? 'bg-[#F08C28]/15 text-[#F08C28] border-l-2 border-[#F08C28]' : ''}`}
                          style={!event.is_featured ? {
                            backgroundColor: `${categoryColors[event.category] || '#005AA0'}15`,
                            color: categoryColors[event.category] || '#005AA0',
                            borderLeft: `2px solid ${categoryColors[event.category] || '#005AA0'}`
                          } : {}}
                          data-testid={`cal-event-${event.id}`}
                        >
                          {event.is_featured && <Star className="w-2.5 h-2.5 inline mr-0.5" />}
                          {event.event_name}
                        </Link>
                      ))}
                      {dayEvents.length > 3 && (
                        <span className="text-xs text-neutral-400 font-medium pl-1">
                          +{dayEvents.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Category Legend */}
            <div className="mt-4 flex flex-wrap gap-3 justify-center">
              {Object.entries(categoryColors).map(([cat, color]) => (
                <div key={cat} className="flex items-center gap-1.5 text-xs text-neutral-500">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }}></span>
                  {cat}
                </div>
              ))}
              <div className="flex items-center gap-1.5 text-xs text-neutral-500">
                <Star className="w-2.5 h-2.5 text-[#F08C28]" />
                Featured
              </div>
            </div>

            {/* Selected Date Events */}
            {selectedDate && (
              <div className="mt-8" data-testid="selected-date-events">
                <h3 className="text-xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  Events on {format(selectedDate, 'MMMM d, yyyy')}
                </h3>
                {filteredEvents.length > 0 ? (
                  <div className="space-y-3">
                    {filteredEvents.map((event) => {
                      const dateInfo = formatEventDate(event.event_date);
                      return (
                        <Link
                          key={event.id}
                          to={`/events/${event.slug || event.id}`}
                          className={`flex bg-white rounded-xl border border-neutral-200 overflow-hidden hover:shadow-md transition-shadow ${event.is_featured ? 'ring-1 ring-[#F08C28]/40' : ''}`}
                          data-testid={`selected-event-${event.id}`}
                        >
                          <div className="w-20 flex-shrink-0 bg-gradient-to-br from-[#005AA0] to-[#004B87] text-white flex flex-col items-center justify-center p-3">
                            <span className="text-xs font-bold uppercase">{dateInfo.month}</span>
                            <span className="text-2xl font-bold">{dateInfo.day}</span>
                          </div>
                          <div className="p-4 flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h4 className="font-bold text-neutral-900 truncate">{event.event_name}</h4>
                              {event.is_featured && (
                                <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-[#F08C28]/10 text-[#F08C28] text-xs font-bold rounded-full">
                                  <Star className="w-3 h-3" /> Featured
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-neutral-500 mb-2">{event.category}</p>
                            <div className="flex flex-wrap gap-4 text-sm text-neutral-600">
                              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {event.start_time}{event.end_time ? ` - ${event.end_time}` : ''}</span>
                              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {event.location}</span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-white rounded-xl border border-neutral-200">
                    <CalendarIcon className="w-10 h-10 text-neutral-300 mx-auto mb-2" />
                    <p className="text-neutral-500">No events on this date</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* ==================== LIST VIEW ==================== */
          <div>
            <div className="mb-6">
              <p className="text-neutral-600">{events.length} upcoming events</p>
            </div>

            {events.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event) => {
                  const dateInfo = formatEventDate(event.event_date);
                  return (
                    <Link
                      key={event.id}
                      to={`/events/${event.slug || event.id}`}
                      className={`group bg-white rounded-2xl border border-neutral-200 overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1 ${event.is_featured ? 'ring-1 ring-[#F08C28]/40' : ''}`}
                      data-testid={`event-card-${event.id}`}
                    >
                      <div className="relative h-40 bg-gradient-to-br from-[#005AA0] to-[#004B87]">
                        {event.image ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${event.image}`}
                            alt={event.event_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-white text-6xl font-bold opacity-20">
                            {event.event_name.charAt(0)}
                          </div>
                        )}
                        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-md px-3 py-1.5 text-center">
                          <span className="block text-xs font-bold text-[#005AA0] uppercase">{dateInfo.month}</span>
                          <span className="block text-xl font-bold text-neutral-900 leading-tight">{dateInfo.day}</span>
                        </div>
                        {event.is_featured && (
                          <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 bg-[#F08C28] text-white text-xs font-bold rounded-full shadow-sm">
                            <Star className="w-3 h-3" /> Featured
                          </span>
                        )}
                      </div>
                      <div className="p-5">
                        <h3 className="font-bold text-lg text-neutral-900 mb-1 group-hover:text-[#005AA0] transition-colors">{event.event_name}</h3>
                        <p className="text-sm text-neutral-500 mb-3">{event.category}</p>
                        <div className="space-y-2 text-sm text-neutral-600">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 flex-shrink-0" />
                            {event.start_time}{event.end_time ? ` - ${event.end_time}` : ''}
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span className="line-clamp-1">{event.location}</span>
                          </div>
                        </div>
                        {event.cost && (
                          <div className="mt-3 pt-3 border-t border-neutral-100">
                            <span className="font-semibold text-[#005AA0]">
                              {event.cost === 'Free' ? 'Free Event' : event.cost}
                            </span>
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-neutral-200">
                <CalendarIcon className="w-14 h-14 text-neutral-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-neutral-900 mb-2">No events found</h3>
                <p className="text-neutral-500 mb-6">Be the first to submit an event!</p>
                <Link to="/submit-event" className="inline-flex items-center gap-2 px-6 py-3 bg-[#005AA0] text-white rounded-full font-semibold hover:bg-[#004B87] transition-colors">
                  Submit Event
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsCalendar;
